import { Icon } from '@natera/material/lib/icon'
import { Command } from 'cmdk'
import { useMemo, useState } from 'react'
import type { GoldSetPhase } from '~/entities/gold-set'
import { cn } from '~/shared/lib/cn'
import { Popover, PopoverContent, PopoverTrigger, Text } from '~/shared/ui'
import { formatPhase, scoreCategorySearch } from '../lib'

export function CategoryPicker({
  phases,
  value,
  onChange,
}: {
  phases: GoldSetPhase[]
  value: string | null
  onChange: (category: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const selected = useMemo(
    () => phases.flatMap((phase) => phase.categories).find((category) => category.label === value),
    [phases, value],
  )

  return (
    <div className="flex flex-col gap-1">
      <Text appearance="SUBTITLE2">
        Category<span className="text-error-main">*</span>
      </Text>
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-haspopup="listbox"
            className={cn(
              'border-input-outlined-enabled-border hover:border-input-outlined-hovered-border flex min-h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-sm border bg-transparent px-4 py-2 text-left outline-none',
              'focus-visible:border-input-outlined-focused-border data-[state=open]:border-input-outlined-focused-border',
            )}
          >
            <span
              className={cn('text-[15px] leading-5 wrap-anywhere', {
                'text-text-tertiary': !value,
              })}
            >
              {value ?? 'Select a category'}
            </span>
            <Icon className="text-text-secondary shrink-0">arrow_drop_down</Icon>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="py-0"
        >
          <Command
            filter={scoreCategorySearch}
            loop
          >
            <div className="border-divider flex items-center gap-3 border-b px-4">
              <Icon className="text-content-high-emphasis shrink-0">search</Icon>
              <Command.Input
                className="w-full border-none bg-transparent py-3 text-[15px] leading-5 outline-none"
                placeholder="Search categories and descriptions"
              />
            </div>
            <Command.List className="max-h-[min(60dvh,520px)] overflow-y-auto py-1">
              <Command.Empty className="text-text-secondary px-4 py-6 text-center text-sm">
                No category matches.
              </Command.Empty>
              {phases.map((phase) => (
                <Command.Group
                  key={phase.phase}
                  heading={formatPhase(phase.phase)}
                  className="[&_[cmdk-group-heading]]:text-text-secondary [&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-[0.4px] [&_[cmdk-group-heading]]:uppercase"
                >
                  {phase.categories.map((category) => (
                    <Command.Item
                      key={category.label}
                      value={category.label}
                      keywords={[phase.phase, category.description ?? '']}
                      title={category.description ?? undefined}
                      onSelect={() => {
                        onChange(category.label)
                        setIsOpen(false)
                      }}
                      className="data-[selected=true]:bg-action-focused flex cursor-pointer items-start gap-2 px-4 py-2 outline-none"
                    >
                      <Icon
                        className={cn('text-primary-main shrink-0 text-[18px]', {
                          invisible: category.label !== value,
                        })}
                      >
                        check
                      </Icon>
                      <div className="flex min-w-0 flex-col">
                        <span className="text-[14px] leading-5 wrap-anywhere">
                          {category.label}
                        </span>
                        {category.description && (
                          <span className="text-text-secondary line-clamp-2 text-xs leading-4">
                            {category.description}
                          </span>
                        )}
                      </div>
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>
      {selected?.description && (
        <Text
          className="text-text-secondary"
          appearance="CAPTION"
        >
          {selected.description}
        </Text>
      )}
    </div>
  )
}
